import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { documentRowClicks } from '@/components/documents/documentUtils'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form'
import { OptionRadioGroup } from '@/containers/Settings/OptionRadioGroup'
import { SettingsFormFooter, SettingsSection } from '@/containers/Settings/SettingsLayout'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'

const schema = z.object({
  documentRowClick: z.enum(documentRowClicks),
})

type NavigationSettingsFormData = z.infer<typeof schema>

const rowClickOptions: Record<NavigationSettingsFormData['documentRowClick'], { label: string; description: string }> = {
  sheet: { label: 'Otevřít boční panel', description: 'Rychlý náhled s akcemi, seznam zůstane otevřený.' },
  detail: { label: 'Otevřít detail dokladu', description: 'Rovnou přejde na celý doklad.' },
}

export function NavigationSettingsForm({
  defaultValues,
  onSuccess,
}: {
  defaultValues: Partial<NavigationSettingsFormData>
  onSuccess: () => void
}) {
  const form = useForm<NavigationSettingsFormData>({
    resolver: zodResolver(schema),
    defaultValues: { documentRowClick: defaultValues.documentRowClick ?? 'sheet' },
  })

  const onSubmit = (data: NavigationSettingsFormData) =>
    API.patch('/api/v2/settings', data)
      .then(() => {
        toast.success('Nastavení uloženo')
        form.reset(data)
        onSuccess()
      })
      .catch((error: unknown) => {
        toast.error('Nastavení se nepodařilo uložit', { description: extractErrorMessage(error) })
      })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="documentRowClick"
          render={({ field }) => (
            <SettingsSection
              title="Kliknutí na doklad v seznamu"
              description="Platí pro seznam faktur, výzev k platbě a nákladů. Na jméno odběratele nebo dodavatele můžete kliknout vždy, otevře se detail kontaktu."
            >
              <FormItem>
                <FormControl>
                  <OptionRadioGroup
                    name="documentRowClick"
                    value={field.value}
                    onChange={field.onChange}
                    options={documentRowClicks.map((mode) => ({ value: mode, ...rowClickOptions[mode] }))}
                  />
                </FormControl>
              </FormItem>
            </SettingsSection>
          )}
        />

        <SettingsFormFooter className="mt-0">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            Uložit
          </Button>
        </SettingsFormFooter>
      </form>
    </Form>
  )
}
